import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FornecedorAutocompleteField from "./FornecedorAutocompleteField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined";
import type { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import { currencyMaskBRL, currencyToNumber } from "helpers/utils";
import { LojaDataGrid } from "../../../common";
import FinanceiroSection from "./FinanceiroSection";
import { FINANCEIRO_ACCENT } from "../../components/financeiro-lancamentos.theme";
import {
  atualizarDespesaRecorrente,
  criarDespesaRecorrente,
  gerarDespesasFixasMes,
  listarDespesasRecorrentes,
} from "../services/contas-pagar.service";
import type { DespesaRecorrenteApi } from "../types/contas-pagar.types";
import type { FornecedorFinanceiroApi } from "../types/contas-pagar.types";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";

type TemplateForm = {
  descricao: string;
  valorTotal: string;
  diaVencimento: string;
  categoria: string;
  contexto: string;
  fornecedorId: string | null;
};

const formVazio = (): TemplateForm => ({
  descricao: "",
  valorTotal: "",
  diaVencimento: "10",
  categoria: "INFRAESTRUTURA",
  contexto: "ADMINISTRATIVO",
  fornecedorId: null,
});

const MESES_OPCOES = [
  { value: "1", label: "Janeiro" },
  { value: "2", label: "Fevereiro" },
  { value: "3", label: "Março" },
  { value: "4", label: "Abril" },
  { value: "5", label: "Maio" },
  { value: "6", label: "Junho" },
  { value: "7", label: "Julho" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" },
];

function labelMes(value: string): string {
  return MESES_OPCOES.find((m) => m.value === value)?.label ?? value;
}

/** Converte valor da API (ex.: "120.00") para dígitos em centavos usados pela máscara. */
function valorApiParaCentavosDigits(valor: string): string {
  const n = Number(String(valor).replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(Math.round(n * 100));
}

const DIA_VENCIMENTO_MIN = 1;
const DIA_VENCIMENTO_MAX = 28;

function validarDiaVencimento(raw: string): string | null {
  const t = raw.trim();
  if (!t) return "Informe o dia de vencimento.";
  if (!/^\d+$/.test(t)) return "Use apenas números.";
  const dia = Number(t);
  if (!Number.isInteger(dia) || dia < DIA_VENCIMENTO_MIN || dia > DIA_VENCIMENTO_MAX) {
    return `O dia deve ser entre ${DIA_VENCIMENTO_MIN} e ${DIA_VENCIMENTO_MAX}.`;
  }
  return null;
}

function PassoBadge({ numero }: { numero: 1 | 2 }) {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: FINANCEIRO_ACCENT,
        color: "#fff",
        fontSize: "0.8125rem",
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {numero}
    </Box>
  );
}

export type DespesasFixasSectionProps = {
  fornecedores: FornecedorFinanceiroApi[];
  onRefetchFornecedores?: () => void;
  onTitulosGerados?: () => void;
  /** Quando false, não busca templates (ex.: modal fechado). */
  enabled?: boolean;
  /** embedded = sem FinanceiroSection (uso dentro de modal). */
  variant?: "section" | "embedded";
};

export default function DespesasFixasSection({
  fornecedores,
  onRefetchFornecedores,
  onTitulosGerados,
  enabled = true,
  variant = "section",
}: DespesasFixasSectionProps) {
  const isEmbedded = variant === "embedded";
  const [rows, setRows] = useState<DespesaRecorrenteApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateForm>(formVazio);
  const [salvando, setSalvando] = useState(false);
  const [gerando, setGerando] = useState(false);

  const agora = new Date();
  const [mesGerar, setMesGerar] = useState(String(agora.getMonth() + 1));
  const [anoGerar, setAnoGerar] = useState(String(agora.getFullYear()));

  const templatesAtivos = useMemo(
    () => rows.filter((row) => row.ativo).length,
    [rows],
  );

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, status } = await listarDespesasRecorrentes();
      if (status !== 200) {
        setError("Não foi possível carregar despesas fixas.");
        setRows([]);
        return;
      }
      setRows(data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Erro ao carregar templates.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void carregar();
  }, [carregar, enabled]);

  const abrirNovo = () => {
    setEditId(null);
    setForm(formVazio());
    setDialogOpen(true);
  };

  const abrirEditar = (row: DespesaRecorrenteApi) => {
    setEditId(row.id);
    setForm({
      descricao: row.descricao,
      valorTotal: valorApiParaCentavosDigits(row.valorTotal),
      diaVencimento: String(row.diaVencimento),
      categoria: row.categoria,
      contexto: row.contexto,
      fornecedorId: row.fornecedorId ?? null,
    });
    setDialogOpen(true);
  };

  const patch =
    (field: keyof TemplateForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const patchMoeda = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const digits = (e.target.value || "").replace(/\D/g, "");
    setForm((prev) => ({ ...prev, valorTotal: digits }));
  };

  const patchDiaVencimento = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let digits = (e.target.value || "").replace(/\D/g, "").slice(0, 2);
    if (!digits) {
      setForm((prev) => ({ ...prev, diaVencimento: "" }));
      return;
    }
    if (digits.length === 1 && digits === "0") return;
    const n = Number(digits);
    if (n > DIA_VENCIMENTO_MAX) digits = String(DIA_VENCIMENTO_MAX);
    setForm((prev) => ({ ...prev, diaVencimento: digits }));
  };

  const diaVencimentoErro = useMemo(() => {
    if (!form.diaVencimento.trim()) return null;
    return validarDiaVencimento(form.diaVencimento);
  }, [form.diaVencimento]);

  const salvar = async () => {
    const valor = currencyToNumber(form.valorTotal);
    const erroDia = validarDiaVencimento(form.diaVencimento);
    if (!form.descricao.trim() || !Number.isFinite(valor) || valor <= 0) {
      setFeedback("Preencha descrição e valor válidos.");
      return;
    }
    if (erroDia) {
      setFeedback(erroDia);
      return;
    }
    const diaVencimento = Number(form.diaVencimento);
    setSalvando(true);
    setFeedback(null);
    try {
      const payload = {
        descricao: form.descricao.trim(),
        valorTotal: valor,
        diaVencimento,
        categoria: form.categoria,
        contexto: form.contexto,
        fornecedorId: form.fornecedorId || null,
      };
      const res = editId
        ? await atualizarDespesaRecorrente(editId, payload)
        : await criarDespesaRecorrente(payload);
      if (res.status === 200 || res.status === 201) {
        setDialogOpen(false);
        setFeedback(editId ? "Despesa fixa atualizada." : "Despesa fixa cadastrada.");
        await carregar();
        return;
      }
      setFeedback("Não foi possível salvar a despesa fixa.");
    } catch (e: unknown) {
      setFeedback(e instanceof Error ? e.message : "Erro ao salvar.");
    } finally {
      setSalvando(false);
    }
  };

  const toggleAtivo = async (row: DespesaRecorrenteApi) => {
    try {
      await atualizarDespesaRecorrente(row.id, { ativo: !row.ativo });
      await carregar();
    } catch {
      setFeedback("Erro ao alterar status da despesa fixa.");
    }
  };

  const gerarMes = async () => {
    const mes = Number(mesGerar);
    const ano = Number(anoGerar);
    if (!Number.isFinite(mes) || mes < 1 || mes > 12) {
      setFeedback("Mês inválido.");
      return;
    }
    setGerando(true);
    setFeedback(null);
    try {
      const { data, status } = await gerarDespesasFixasMes(mes, ano);
      if (status === 200 && data) {
        setFeedback(
          `${data.gerados} título(s) gerado(s), ${data.ignorados} já existente(s).`
        );
        onTitulosGerados?.();
        await carregar();
        return;
      }
      setFeedback("Não foi possível gerar os títulos do mês.");
    } catch (e: unknown) {
      setFeedback(e instanceof Error ? e.message : "Erro ao gerar títulos.");
    } finally {
      setGerando(false);
    }
  };

  const columns: GridColDef<DespesaRecorrenteApi>[] = [
    { field: "descricao", headerName: "Descrição", flex: 1.4, minWidth: 200 },
    {
      field: "valorTotal",
      headerName: "Valor",
      width: 120,
      valueFormatter: (p) => formatarMoedaBRL(Number(p.value)),
    },
    { field: "diaVencimento", headerName: "Dia", width: 72 },
    {
      field: "fornecedor",
      headerName: "Fornecedor",
      flex: 1,
      minWidth: 160,
      valueGetter: (params: GridValueGetterParams<DespesaRecorrenteApi>) =>
        params.row.fornecedor?.nome ?? "—",
    },
    {
      field: "ativo",
      headerName: "Status",
      width: 96,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.row.ativo ? "Ativo" : "Inativo"}
          color={p.row.ativo ? "success" : "default"}
          variant={p.row.ativo ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "acoes",
      headerName: "",
      width: 188,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            onClick={() => abrirEditar(p.row)}
          >
            Editar
          </MDButton>
          <MDButton
            size="small"
            variant="text"
            color={p.row.ativo ? "warning" : "success"}
            onClick={() => void toggleAtivo(p.row)}
          >
            {p.row.ativo ? "Desativar" : "Ativar"}
          </MDButton>
        </Stack>
      ),
    },
  ];

  const conteudo = (
    <Stack spacing={isEmbedded ? 2 : 3}>
      {isEmbedded ? (
        <Alert
          severity="info"
          icon={<EventRepeatOutlinedIcon fontSize="inherit" />}
          sx={{ py: 0.5, alignItems: "center" }}
        >
          <Typography variant="body2">
            <strong>Passo 1:</strong> cadastre despesas mensais.{" "}
            <strong>Passo 2:</strong> escolha o mês e gere os títulos em Contas a
            pagar.
          </Typography>
        </Alert>
      ) : null}

      {feedback ? (
        <Alert severity="info" onClose={() => setFeedback(null)}>
          {feedback}
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {/* Passo 1 — templates */}
      <Paper
        variant="outlined"
        sx={{
          p: isEmbedded ? 2 : 2.5,
          borderRadius: 2,
          borderColor: "grey.200",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          sx={{ mb: isEmbedded ? 1.5 : 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <PassoBadge numero={1} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Cadastre suas despesas fixas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Ex.: aluguel, energia, internet. Informe valor e dia de vencimento.
                {templatesAtivos > 0
                  ? ` ${templatesAtivos} ativa(s) de ${rows.length} cadastrada(s).`
                  : ""}
              </Typography>
            </Box>
          </Stack>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={abrirNovo}
            startIcon={<AddOutlinedIcon />}
            sx={{ flexShrink: 0, alignSelf: "center" }}
          >
            Adicionar despesa fixa
          </MDButton>
        </Stack>

        {loading ? (
          <Skeleton variant="rounded" height={isEmbedded ? 160 : 200} />
        ) : rows.length === 0 ? (
          <Box
            sx={{
              py: isEmbedded ? 3 : 5,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
              bgcolor: "grey.50",
            }}
          >
            <PlaylistAddOutlinedIcon
              sx={{ fontSize: 40, color: "grey.400", mb: 1 }}
            />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Nenhuma despesa fixa cadastrada
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 420, mx: "auto" }}>
              Comece adicionando as contas que você paga todo mês. Depois você poderá
              gerá-las automaticamente na lista de contas a pagar.
            </Typography>
            <MDButton
              variant="outlined"
              color="dark"
              size="small"
              onClick={abrirNovo}
              startIcon={<AddOutlinedIcon />}
            >
              Adicionar primeira despesa
            </MDButton>
          </Box>
        ) : (
          <LojaDataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            autoHeight
            hideFooter={rows.length <= 10}
            disableColumnMenu
          />
        )}
      </Paper>

      {/* Passo 2 — gerar títulos */}
      <Paper
        variant="outlined"
        sx={{
          p: isEmbedded ? 2 : 2.5,
          borderRadius: 2,
          borderColor: "grey.200",
          borderLeft: `4px solid ${FINANCEIRO_ACCENT}`,
          bgcolor: "rgba(249,96,48,0.03)",
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mb: isEmbedded ? 1.5 : 2 }}
        >
          <PassoBadge numero={2} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Gerar contas do mês
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Cria os títulos a pagar em{" "}
              <strong>
                {labelMes(mesGerar)} de {anoGerar}
              </strong>{" "}
              com base nas despesas <strong>ativas</strong>. Títulos já gerados não
              são duplicados.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={isEmbedded ? "row" : { xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={isEmbedded ? "flex-end" : { xs: "stretch", sm: "flex-end" }}
        >
          <FormField
            select
            label="Mês de referência"
            value={mesGerar}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setMesGerar(e.target.value)
            }
            fullWidth
            sx={{
              flex: 1,
              minWidth: isEmbedded ? 180 : 0,
              maxWidth: isEmbedded ? 280 : undefined,
              m: 0,
            }}
            size="small"
            InputProps={{ style: { height: 44 } }}
          >
            {MESES_OPCOES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </FormField>
          <FormField
            label="Ano"
            value={anoGerar}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setAnoGerar(e.target.value)
            }
            fullWidth={false}
            sx={{
              width: isEmbedded ? 100 : { xs: "100%", sm: 110 },
              flexShrink: 0,
              m: 0,
            }}
            size="small"
            InputProps={{ style: { height: 44 } }}
          />
          <MDButton
            variant="gradient"
            color="dark"
            size="medium"
            onClick={() => void gerarMes()}
            disabled={gerando || templatesAtivos === 0}
            startIcon={<CalendarMonthOutlinedIcon />}
            sx={{
              flexShrink: 0,
              whiteSpace: "nowrap",
              minHeight: 44,
              background: `linear-gradient(195deg, ${FINANCEIRO_ACCENT}, #e04a1a)`,
            }}
          >
            {gerando ? "Gerando…" : "Gerar contas a pagar"}
          </MDButton>
        </Stack>

        {templatesAtivos === 0 && !loading ? (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1.5, display: "block" }}>
            Cadastre e ative ao menos uma despesa fixa antes de gerar os títulos.
          </Typography>
        ) : null}
      </Paper>
    </Stack>
  );

  return (
    <>
      {variant === "section" ? (
        <FinanceiroSection
          titulo="Despesas fixas mensais"
          subtitulo="Cadastre contas recorrentes e gere os títulos a pagar do mês."
        >
          {conteudo}
        </FinanceiroSection>
      ) : (
        conteudo
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !salvando && setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {editId ? "Editar despesa fixa" : "Nova despesa fixa"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Esta despesa será usada para gerar automaticamente as contas a pagar de
            cada mês.
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <FormField
                label="Descrição"
                value={form.descricao}
                onChange={patch("descricao")}
                fullWidth
                placeholder="Ex.: Água, Energia, Aluguel"
                InputProps={{ style: { height: 44 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormField
                label="Valor (R$)"
                value={currencyMaskBRL(form.valorTotal ?? "0")}
                onChange={patchMoeda}
                fullWidth
                placeholder="0,00"
                inputProps={{ inputMode: "numeric" }}
                InputProps={{ style: { height: 44 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormField
                label="Dia vencimento (1–28)"
                value={form.diaVencimento}
                onChange={patchDiaVencimento}
                fullWidth
                error={Boolean(diaVencimentoErro)}
                helperText={diaVencimentoErro ?? undefined}
                placeholder={`${DIA_VENCIMENTO_MIN} a ${DIA_VENCIMENTO_MAX}`}
                inputProps={{
                  inputMode: "numeric",
                  min: DIA_VENCIMENTO_MIN,
                  max: DIA_VENCIMENTO_MAX,
                  pattern: "[0-9]*",
                }}
                InputProps={{ style: { height: 44 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormField
                select
                label="Categoria"
                value={form.categoria}
                onChange={patch("categoria")}
                fullWidth
                InputProps={{ style: { height: 44 } }}
              >
                <MenuItem value="INFRAESTRUTURA">Infraestrutura</MenuItem>
                <MenuItem value="SERVICOS">Serviços</MenuItem>
                <MenuItem value="OPERACIONAL">Operacional</MenuItem>
                <MenuItem value="OUTROS">Outros</MenuItem>
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField
                select
                label="Contexto"
                value={form.contexto}
                onChange={patch("contexto")}
                fullWidth
                InputProps={{ style: { height: 44 } }}
              >
                <MenuItem value="ADMINISTRATIVO">Administrativo</MenuItem>
                <MenuItem value="LOJA">Loja</MenuItem>
                <MenuItem value="OPERACIONAL">Operacional</MenuItem>
              </FormField>
            </Grid>
            <Grid item xs={12}>
              <FornecedorAutocompleteField
                fornecedores={fornecedores}
                value={form.fornecedorId}
                onChange={(id) => setForm((p) => ({ ...p, fornecedorId: id }))}
                onRefetchFornecedores={onRefetchFornecedores}
                label="Fornecedor (opcional)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setDialogOpen(false)}
            disabled={salvando}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => void salvar()}
            disabled={salvando}
          >
            {salvando ? "Salvando…" : "Salvar despesa fixa"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
